import { apiSlice } from "../baseApi";
import { requestFreshLiveAlertsCount } from "./adminV2LiveAlertCountEndpoints";

/** Work stays under the contact home; this optional scope pins existing email.
 * Omitted scope retains the legacy same-home contract; explicit null is distinct. */
export interface ContactNextActionCommunicationReference {
  messageId: string;
  recipientId: string;
  organisationId?: string | null;
}
export interface ContactNextActionInput {
  clientRequestId: string;
  action: string;
  organisationId: string | null;
  dueAt: string | null;
  unscheduledReason: string | null;
  communication?: ContactNextActionCommunicationReference;
  fhcCapture?: { submissionId: string };
}
export interface ContactNextAction {
  id: string;
  action: string;
  ownerStaffUserId: string | null;
  ownerDisplayName: string | null;
  dueAt: string | null;
  unscheduledReason: string | null;
  state: string;
  createdAt: string;
  completedAt: string | null;
  waitingUntil: string | null;
  communication: ContactNextActionCommunicationReference | null;
  sourcePath: string;
  fhcCapture?: { submissionId: string };
}
/** Read-only original-request context. Neither state completes or rekeys a save. */
export interface ContactNextActionReconcileInput {
  clientRequestId: string;
  organisationId: string | null;
}
export type ContactNextActionReconcileResponse = {
  clientRequestId: string;
  personId: string;
  organisationId: string | null;
  currentOrganisationId: string | null;
} & ({ state: "readable"; item: ContactNextAction } | { state: "unconfirmed" });
const nextActionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    reconcileAdminV2ContactNextAction: builder.mutation<
      ContactNextActionReconcileResponse,
      { personId: string; body: ContactNextActionReconcileInput }
    >({
      query: ({ personId, body }) => ({
        url: `/admin/v2/crm/people/${encodeURIComponent(personId)}/next-actions/reconcile`,
        method: "POST",
        data: body,
      }),
      // A read-context response does not invalidate work or clear a pending save.
    }),
    getAdminV2ContactNextActions: builder.query<
      { items: ContactNextAction[]; hasMore: boolean },
      // Cache identity only. Never forwarded as actor, permission or scope.
      { personId: string; offset?: number; readerKey: string }
    >({
      query: ({ personId, offset }) => ({
        url: `/admin/v2/crm/people/${encodeURIComponent(personId)}/next-actions`,
        params: { offset: offset ?? 0 },
      }),
      providesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: personId },
        { type: "AdminV2LiveAlerts", id: "LIST" },
      ],
      keepUnusedDataFor: 0,
    }),
    createAdminV2ContactNextAction: builder.mutation<
      {
        item: ContactNextAction;
        wasIdempotentReplay: boolean;
        clientRequestId: string;
        personId: string;
        organisationId: string | null;
      },
      { personId: string; body: ContactNextActionInput }
    >({
      query: ({ personId, body }) => ({
        url: `/admin/v2/crm/people/${encodeURIComponent(personId)}/next-actions`,
        method: "POST",
        data: body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        requestFreshLiveAlertsCount();
        dispatch(
          apiSlice.util.invalidateTags([
            { type: "AdminV2LiveAlerts", id: "COUNT" },
          ]),
        );
      },
      invalidatesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: personId },
        { type: "AdminV2LiveAlerts", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),
  }),
});
export const {
  useGetAdminV2ContactNextActionsQuery,
  useCreateAdminV2ContactNextActionMutation,
  useReconcileAdminV2ContactNextActionMutation,
} = nextActionsApi;
